import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Table3, { columnHelper } from '../../../containers/Table/Table3';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import Strings from '../../../Strings';
import { GET_ATHENA_CLAIM_HISTORY_RESULT } from './actions';

const eventColumns = [
  columnHelper.accessor('type', { header: Strings.athenaClaimSubmissionStatus }),
  columnHelper.accessor('attemptCount', { header: Strings.athenaClaimSubmissionAttempts }),
  columnHelper.accessor('athenaClaimId', { header: Strings.athenaClaimSubmissionAthenaClaimId }),
  columnHelper.accessor('errorMessage', { header: Strings.athenaClaimSubmissionError }),
  columnHelper.accessor('actedBy', { header: Strings.athenaClaimHistoryActor }),
  columnHelper.accessor('createdAt', { header: Strings.createdAt }),
];
const observationColumns = [
  columnHelper.accessor('state', { header: Strings.athenaClaimSubmissionStatus }),
  columnHelper.accessor('athenaClaimId', { header: Strings.athenaClaimSubmissionAthenaClaimId }),
  columnHelper.accessor('mappingVersion', { header: Strings.athenaClaimHistoryMappingVersion }),
  columnHelper.display({
    id: 'payers',
    header: Strings.athenaClaimSubmissionExternalStatusNote,
    cell: info => {
      const observation = info.row.original;
      return ['patient', 'primary', 'secondary'].map(payer => `${payer}: ${observation[`${payer}PayerStatus`] || '-'}; ${observation[`${payer}PayerNote`] || '-'}`).join(' | ');
    },
  }),
  columnHelper.accessor('observedAt', { header: Strings.athenaClaimSubmissionLastPolledAt }),
];

export default function ClaimSubmissionHistoryModal({ organizationId, submissionId, getHistory, onClose, showNotification }) {
  const [pages, setPages] = useState([{}]);
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const current = pages[pages.length - 1];

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setHistory(null);
    getHistory(organizationId, submissionId, current).then(response => {
      if (!active) return;
      if (response.type === GET_ATHENA_CLAIM_HISTORY_RESULT) {
        setHistory(response.response);
      } else {
        showNotification(Strings.athenaClaimSubmissionsLoadFailed, 5000, true);
      }
    }).catch(() => {
      if (active) showNotification(Strings.athenaClaimSubmissionsLoadFailed, 5000, true);
    }).finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [organizationId, submissionId, current]);

  const older = () => setPages([...pages, {
    eventsBefore: history.nextEventsBefore || 1,
    observationsBefore: history.nextObservationsBefore || 1,
  }]);

  return (
    <SimpleModal
      name="athena-claim-history-modal"
      title={`${Strings.history} — #${submissionId}`}
      onCancel={onClose}
      customButtons={<div className="buttons"><button type="button" className="brand-white-gray" onClick={onClose}>{Strings.close}</button></div>}
      content={(
        <>
          <h3>{Strings.athenaClaimHistoryEvents}</h3>
          <Table3
            name="athena-claim-events"
            data={history?.events || []}
            columns={eventColumns}
            pagination={{ offset: 0, limit: 50, totalRecords: history?.events.length || 0 }}
            isLoading={isLoading}
            noPagination
          />
          <h3>{Strings.athenaClaimHistoryObservations}</h3>
          <Table3
            name="athena-claim-observations"
            data={history?.observations || []}
            columns={observationColumns}
            pagination={{ offset: 0, limit: 50, totalRecords: history?.observations.length || 0 }}
            isLoading={isLoading}
            noPagination
          />
          <div className="buttons">
            <button type="button" disabled={isLoading || pages.length === 1} onClick={() => setPages(pages.slice(0, -1))}>{Strings.previous}</button>
            <button type="button" disabled={isLoading || (!history?.nextEventsBefore && !history?.nextObservationsBefore)} onClick={older}>{Strings.athenaClaimHistoryOlder}</button>
          </div>
        </>
      )}
    />
  );
}

ClaimSubmissionHistoryModal.propTypes = {
  organizationId: PropTypes.string.isRequired,
  submissionId: PropTypes.number.isRequired,
  getHistory: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};
